import { Controller } from "@hotwired/stimulus"

// DM-only pin editing on a map: click empty map to place a pin, drag a pin to move it.
// Coordinates are percentages of the image (ADR-0106). The form is server-rendered;
// this controller only fills in x/y and sends moves.
export default class extends Controller {
  static targets = ["canvas", "pin", "panel", "x", "y", "marker", "toggle", "hint", "label"]
  static values = { editing: Boolean }

  connect() {
    this.dragging = null
    this.moved = false
    this.editingValueChanged()
  }

  toggle() {
    this.editingValue = !this.editingValue
  }

  editingValueChanged() {
    if (!this.hasCanvasTarget) return
    this.canvasTarget.classList.toggle("cursor-crosshair", this.editingValue)
    if (this.hasHintTarget) this.hintTarget.hidden = !this.editingValue
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-pressed", String(this.editingValue))
    }
    if (!this.editingValue) this.closePanel()
  }

  place(event) {
    if (!this.editingValue || this.dragging) return
    if (event.target.closest("[data-map-editor-target='pin']")) return
    this.dispatch("placing")
    const { x, y } = this.percentAt(event)
    this.xTarget.value = x
    this.yTarget.value = y
    this.markerTarget.style.left = `${x}%`
    this.markerTarget.style.top = `${y}%`
    this.markerTarget.hidden = false
    this.panelTarget.hidden = false
    if (this.hasLabelTarget) this.labelTarget.focus()
  }

  closePanel() {
    if (this.hasPanelTarget) this.panelTarget.hidden = true
    if (this.hasMarkerTarget) this.markerTarget.hidden = true
  }

  startDrag(event) {
    if (!this.editingValue) return
    const pin = event.currentTarget
    event.preventDefault()
    event.stopPropagation()
    pin.setPointerCapture(event.pointerId)
    this.dragging = { pin, startX: event.clientX, startY: event.clientY, left: pin.style.left, top: pin.style.top }
    this.moved = false
  }

  drag(event) {
    if (!this.dragging) return
    if (Math.abs(event.clientX - this.dragging.startX) + Math.abs(event.clientY - this.dragging.startY) > 3) this.moved = true
    if (!this.moved) return
    const { x, y } = this.percentAt(event)
    this.dragging.pin.style.left = `${x}%`
    this.dragging.pin.style.top = `${y}%`
  }

  async endDrag(event) {
    if (!this.dragging) return
    const { pin, left, top } = this.dragging
    this.dragging = null
    if (!this.moved) return
    const { x, y } = this.percentAt(event)
    const response = await fetch(pin.dataset.moveUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ map_pin: { x, y } })
    })
    if (!response.ok) {
      pin.style.left = left
      pin.style.top = top
    }
  }

  // A drag ends with a click; swallow it so the pin does not open.
  pinClick(event) {
    if (this.moved) {
      event.preventDefault()
      this.moved = false
    }
  }

  percentAt(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    const clamp = (value) => Math.min(100, Math.max(0, value))
    return {
      x: Math.round(clamp(((event.clientX - rect.left) / rect.width) * 100) * 10) / 10,
      y: Math.round(clamp(((event.clientY - rect.top) / rect.height) * 100) * 10) / 10
    }
  }
}
